/**
 * Which readings of a `cs-trend-band-chart` get a visible marker (#6676). The readings are the
 * only place a weigh-in is drawn, so the chart must never go blank where the trend line is
 * missing:
 * - 31 readings or fewer (any 2- or 4-week range, a sparse 12 weeks): a marker on every reading;
 * - more: no marker, except on a reading whose day has no trend value (a gap in the line, a
 *   stretch with too few readings), which keeps its marker.
 * A hidden marker still answers the pointer, so its tooltip shows at every range.
 * See design-docs/coaching-dashboard.md, "How the Body Composition cards look (#6676)".
 */

/** above this many readings, only the readings in a gap of the trend line keep their marker */
export const MAX_READINGS_WITH_MARKERS = 31;

/**
 * The marker radius of each reading, in the order given: `radius` where it shows, 0 where it is
 * hidden. `rows` are the trend's rows, oldest first; a row whose `mid` is not a number is a gap,
 * and a day before the first or after the last drawn row has no line either.
 */
export function readingMarkerRadii(
  points: readonly { dateIso: string }[],
  rows: readonly { dateIso: string; mid?: number }[],
  radius: number,
): number[] {
  if (points.length <= MAX_READINGS_WITH_MARKERS) {
    return points.map(() => radius);
  }
  const midByDay = new Map(rows.map(row => [row.dateIso, row.mid]));
  const drawnDays = rows.filter(row => Number.isFinite(row.mid)).map(row => row.dateIso);
  const firstDrawn = drawnDays[0];
  const lastDrawn = drawnDays[drawnDays.length - 1];
  return points.map(point => {
    // a day between two drawn rows with no row of its own lies on the straight stretch between them
    const onLine = firstDrawn !== undefined && point.dateIso >= firstDrawn && point.dateIso <= lastDrawn
      && (!midByDay.has(point.dateIso) || Number.isFinite(midByDay.get(point.dateIso)));
    return onLine ? 0 : radius;
  });
}
